import type {
  CreateUpstreamRequest,
  GetUpstreamSkuRoutingSummaryResponse,
  PreviewUpstreamCapabilityMutationRequest,
  PreviewUpstreamCapabilityMutationResponse,
  CreateUpstreamChannelRequest,
  DeleteUpstreamChannelResponse,
  DiscoverUpstreamCapabilitiesRequest,
  ProbeResultRecord,
  SetUpstreamCapabilityProbeProtocolRequest,
  UpdateUpstreamChannelRequest,
  UpdateUpstreamRequest,
  UpstreamChannelRecord,
  UpstreamModelCapabilityRecord,
  UpstreamCatalogResponse,
  UpstreamConfig,
  UpstreamRetirementImpactResponse,
  UpstreamSkuRoutingIdentityFilter,
  UpstreamSkuCostRecord,
  UpsertUpstreamSkuCostEntry,
  UpsertUpstreamSkuCostsResponse,
} from '@nadoutong/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { invalidateRoutingTargetDisplayNames } from './routingTargetDisplayNames.ts';
import {
  batchIgnoreUpstreamCapabilities,
  batchProbeUpstreamCapabilities,
  batchSetUpstreamCapabilityProbeProtocol,
  batchUnignoreUpstreamCapabilities,
  createUpstream,
  createUpstreamChannel,
  deleteUpstream,
  deleteUpstreamChannel,
  discoverUpstreamCapabilities,
  getUpstream,
  getUpstreamSkuRoutingSummary,
  getUpstreamRetirementImpact,
  ignoreUpstreamCapability,
  listUpstreamSkuCosts,
  listUpstreamChannels,
  listCatalog,
  listProbeResults,
  listUpstreamCapabilities,
  listUpstreams,
  loadUpstreamSkuRoutingIdentitySummary,
  loadTargetedUpstreamSkuRoutingIdentitySummary,
  probeUpstreamCapability,
  previewUpstreamCapabilityMutation,
  setUpstreamCapabilityProbeProtocol,
  setUpstreamSkuCosts,
  unignoreUpstreamCapability,
  updateUpstream,
  updateUpstreamChannel,
} from './upstreams.ts';
import type {
  LoadedUpstreamSkuRoutingSummary,
} from './upstreams.ts';

/**
 * React Query wrappers for the upstreams API.
 *
 * Query key conventions:
 *   - `['upstreams']`            — list query
 *   - `['upstreams', id]`        — single upstream query
 *   - `['upstreams', 'catalog']` — global catalog (per-upstream route map)
 *
 * Mutation invalidation map:
 *   - create / update / delete → invalidate `['upstreams']` + specific id +
 *     catalog. Delete also marks the specific id stale so the detail page
 *     re-fetches and sees soft-delete state.
 *   - capability and route-binding mutations invalidate their own query keys
 *     plus catalog when they affect the routable model view.
 *
 * The hooks read `fetchImpl` from the session context; tests inject a
 * stub there (same pattern as Wave 5A).
 */

export const UPSTREAMS_QUERY_KEY = ['upstreams'] as const;
export const UPSTREAMS_CATALOG_QUERY_KEY = ['upstreams', 'catalog'] as const;

export function upstreamQueryKey(id: string): readonly ['upstreams', string] {
  return ['upstreams', id] as const;
}

export function upstreamSkuCostsQueryKey(
  id: string,
  upstreamChannelId: string,
): readonly ['upstreams', string, 'sku-costs', string] {
  return ['upstreams', id, 'sku-costs', upstreamChannelId] as const;
}

export function upstreamChannelsQueryKey(id: string): readonly ['upstreams', string, 'channels'] {
  return ['upstreams', id, 'channels'] as const;
}

export function useUpstreamsList(): UseQueryResult<UpstreamConfig[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: UPSTREAMS_QUERY_KEY,
    queryFn: ({ signal }) => listUpstreams(fetchImpl, signal),
  });
}

export function useUpstream(id: string | undefined): UseQueryResult<UpstreamConfig> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: upstreamQueryKey(id ?? ''),
    queryFn: ({ signal }) => {
      if (id === undefined || id.length === 0) {
        throw new Error('useUpstream: id is required');
      }
      return getUpstream(id, fetchImpl, signal);
    },
    enabled: id !== undefined && id.length > 0,
  });
}

export function useUpstreamSkuCosts(
  id: string,
  upstreamChannelId: string,
): UseQueryResult<UpstreamSkuCostRecord[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: upstreamSkuCostsQueryKey(id, upstreamChannelId),
    queryFn: ({ signal }) => listUpstreamSkuCosts(id, upstreamChannelId, fetchImpl, signal),
    enabled: id.length > 0 && upstreamChannelId.length > 0,
  });
}

export function useSetUpstreamSkuCosts(
  id: string,
): UseMutationResult<UpsertUpstreamSkuCostsResponse, Error, readonly UpsertUpstreamSkuCostEntry[]> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (costs) => setUpstreamSkuCosts(id, costs, fetchImpl),
    onSuccess: (_response, costs) => {
      const channelIds = new Set(costs.map((cost) => cost.upstream_channel_id));
      for (const channelId of channelIds) {
        void qc.invalidateQueries({ queryKey: upstreamSkuCostsQueryKey(id, channelId) });
      }
    },
  });
}

export function useUpstreamChannels(
  id: string,
): UseQueryResult<UpstreamChannelRecord[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: upstreamChannelsQueryKey(id),
    queryFn: ({ signal }) => listUpstreamChannels(id, fetchImpl, signal),
    enabled: id.length > 0,
  });
}

export function useCreateUpstreamChannel(
  id: string,
): UseMutationResult<UpstreamChannelRecord, Error, CreateUpstreamChannelRequest> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => createUpstreamChannel(id, body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: upstreamChannelsQueryKey(id) });
    },
  });
}

export function useUpdateUpstreamChannel(
  id: string,
): UseMutationResult<
  UpstreamChannelRecord,
  Error,
  { channelId: string; body: UpdateUpstreamChannelRequest }
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ channelId, body }) => updateUpstreamChannel(id, channelId, body, fetchImpl),
    onSuccess: () => {
      invalidateRoutingTargetDisplayNames(qc);
      void qc.invalidateQueries({ queryKey: upstreamChannelsQueryKey(id) });
    },
  });
}

export function useDeleteUpstreamChannel(
  id: string,
): UseMutationResult<DeleteUpstreamChannelResponse, Error, string> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (channelId) => deleteUpstreamChannel(id, channelId, fetchImpl),
    onSuccess: () => {
      invalidateRoutingTargetDisplayNames(qc);
      void qc.invalidateQueries({ queryKey: upstreamChannelsQueryKey(id) });
    },
  });
}

export function useUpstreamCatalog(): UseQueryResult<UpstreamCatalogResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: UPSTREAMS_CATALOG_QUERY_KEY,
    queryFn: ({ signal }) => listCatalog(fetchImpl, signal),
  });
}

/**
 * Retirement-impact summary consumed by the DangerZone confirmation
 * dialog. `enabled` is surfaced so the caller can gate the fetch on
 * dialog open (we don't want to query on every UpstreamDetail mount
 * — the data is only relevant when the operator is about to delete).
 */
export function useUpstreamRetirementImpact(
  id: string | undefined,
  options: { readonly enabled: boolean } = { enabled: true },
): UseQueryResult<UpstreamRetirementImpactResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: ['upstreams', id ?? '', 'retirement-impact'] as const,
    queryFn: ({ signal }) => {
      if (id === undefined || id.length === 0) {
        throw new Error('useUpstreamRetirementImpact: id is required');
      }
      return getUpstreamRetirementImpact(id, fetchImpl, signal);
    },
    enabled: options.enabled && id !== undefined && id.length > 0,
    // Retirement impact is safety-critical: the operator is about to
    // soft-delete an upstream based on what this dialog shows. A
    // concurrent attach / detach / peer deletion must not be masked
    // by a cached snapshot. `staleTime: 0` + `refetchOnMount: 'always'`
    // means each dialog open re-reads from the backend; the query is
    // three indexed SQL reads so the cost is negligible.
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

export function useCreateUpstream(): UseMutationResult<
  UpstreamConfig,
  Error,
  CreateUpstreamRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => createUpstream(body, fetchImpl),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: UPSTREAMS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: UPSTREAMS_CATALOG_QUERY_KEY });
    },
  });
}

export function useUpdateUpstream(
  id: string,
): UseMutationResult<UpstreamConfig, Error, UpdateUpstreamRequest> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body) => updateUpstream(id, body, fetchImpl),
    onSuccess: () => {
      invalidateRoutingTargetDisplayNames(qc);
      void qc.invalidateQueries({ queryKey: UPSTREAMS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: upstreamQueryKey(id) });
      void qc.invalidateQueries({ queryKey: UPSTREAMS_CATALOG_QUERY_KEY });
    },
  });
}

export function useDeleteUpstream(
  id: string,
): UseMutationResult<void, Error, void> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      await deleteUpstream(id, fetchImpl);
    },
    onSuccess: () => {
      invalidateRoutingTargetDisplayNames(qc);
      void qc.invalidateQueries({ queryKey: UPSTREAMS_QUERY_KEY });
      void qc.invalidateQueries({ queryKey: upstreamQueryKey(id) });
      void qc.invalidateQueries({ queryKey: UPSTREAMS_CATALOG_QUERY_KEY });
    },
  });
}

export function probeResultsQueryKey(id: string): readonly ['upstreams', string, 'probes'] {
  return ['upstreams', id, 'probes'] as const;
}

export function upstreamCapabilitiesQueryKey(id: string): readonly ['upstreams', string, 'capabilities'] {
  return ['upstreams', id, 'capabilities'] as const;
}

export function skuRoutingSummaryQueryKey(id: string): readonly ['upstreams', string, 'sku-routing-summary'] {
  return ['upstreams', id, 'sku-routing-summary'] as const;
}

export function skuRoutingIdentitySummaryQueryKey(
  id: string,
  identity: UpstreamSkuRoutingIdentityFilter | null,
): readonly ['upstreams', string, 'sku-routing-summary', 'identity', string, string, string, string, string, string] {
  return [
    'upstreams',
    id,
    'sku-routing-summary',
    'identity',
    identity?.capability_id ?? '',
    identity?.vendor ?? '',
    identity?.model ?? '',
    identity?.account_id ?? '',
    identity?.key_bucket_id ?? '',
    identity?.upstream_channel_id ?? '',
  ] as const;
}

export function skuRoutingTargetedSummaryQueryKey(
  id: string,
  identity: UpstreamSkuRoutingIdentityFilter | null,
  skuId: string | null,
): readonly [
  'upstreams', string, 'sku-routing-summary', 'identity',
  string, string, string, string, string, string, 'sku', string,
] {
  return [
    ...skuRoutingIdentitySummaryQueryKey(id, identity),
    'sku',
    skuId ?? '',
  ] as const;
}

export function useProbeResults(
  id: string,
): UseQueryResult<ProbeResultRecord[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: probeResultsQueryKey(id),
    queryFn: ({ signal }) => listProbeResults(id, fetchImpl, signal),
  });
}

export function useUpstreamCapabilities(
  id: string,
): UseQueryResult<UpstreamModelCapabilityRecord[]> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: upstreamCapabilitiesQueryKey(id),
    queryFn: ({ signal }) => listUpstreamCapabilities(id, fetchImpl, signal),
    enabled: id.length > 0,
  });
}

export function useUpstreamSkuRoutingSummary(
  id: string,
): UseQueryResult<GetUpstreamSkuRoutingSummaryResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: skuRoutingSummaryQueryKey(id),
    queryFn: ({ signal }) => getUpstreamSkuRoutingSummary(id, fetchImpl, {}, signal),
    enabled: id.length > 0,
  });
}

export function useUpstreamSkuRoutingIdentitySummary(
  id: string,
  identity: UpstreamSkuRoutingIdentityFilter | null,
): UseQueryResult<LoadedUpstreamSkuRoutingSummary> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: skuRoutingIdentitySummaryQueryKey(id, identity),
    queryFn: ({ signal }) => {
      if (identity === null) throw new Error('SKU routing identity is required');
      return loadUpstreamSkuRoutingIdentitySummary(id, identity, fetchImpl, signal);
    },
    enabled: id.length > 0 && identity !== null,
  });
}

export function useUpstreamSkuRoutingTargetedSummary(
  id: string,
  identity: UpstreamSkuRoutingIdentityFilter | null,
  skuId: string | null,
): UseQueryResult<LoadedUpstreamSkuRoutingSummary> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: skuRoutingTargetedSummaryQueryKey(id, identity, skuId),
    queryFn: ({ signal }) => {
      if (identity === null || skuId === null) throw new Error('targeted SKU routing identity is required');
      return loadTargetedUpstreamSkuRoutingIdentitySummary(id, identity, skuId, fetchImpl, signal);
    },
    enabled: id.length > 0 && identity !== null && skuId !== null,
    retry: false,
  });
}

export function usePreviewUpstreamCapabilityMutation(
  id: string,
): UseMutationResult<
  PreviewUpstreamCapabilityMutationResponse,
  Error,
  PreviewUpstreamCapabilityMutationRequest
> {
  const { fetchImpl } = useSession();
  return useMutation({
    mutationFn: (body) => previewUpstreamCapabilityMutation(id, body, fetchImpl),
  });
}

export function useDiscoverUpstreamCapabilities(
  id: string,
): UseMutationResult<
  UpstreamModelCapabilityRecord[],
  Error,
  DiscoverUpstreamCapabilitiesRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body) => {
      const res = await discoverUpstreamCapabilities(id, body, fetchImpl);
      return [...res.capabilities];
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: upstreamCapabilitiesQueryKey(id) });
      void qc.invalidateQueries({ queryKey: UPSTREAMS_CATALOG_QUERY_KEY });
    },
  });
}

export function useProbeUpstreamCapability(
  id: string,
): UseMutationResult<UpstreamModelCapabilityRecord, Error, string> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (capabilityId) => {
      const res = await probeUpstreamCapability(id, capabilityId, fetchImpl);
      return res.results[0]!.capability;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: upstreamCapabilitiesQueryKey(id) });
      void qc.invalidateQueries({ queryKey: UPSTREAMS_CATALOG_QUERY_KEY });
    },
  });
}

export function useSetUpstreamCapabilityProbeProtocol(
  id: string,
): UseMutationResult<
  UpstreamModelCapabilityRecord,
  Error,
  { capability_id: string } & SetUpstreamCapabilityProbeProtocolRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ capability_id, ...body }) => {
      const res = await setUpstreamCapabilityProbeProtocol(id, capability_id, body, fetchImpl);
      return res.results[0]!.capability;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: upstreamCapabilitiesQueryKey(id) });
      void qc.invalidateQueries({ queryKey: UPSTREAMS_CATALOG_QUERY_KEY });
    },
  });
}

function invalidateCapabilityQueries(
  qc: ReturnType<typeof useQueryClient>,
  id: string,
): void {
  void qc.invalidateQueries({ queryKey: upstreamCapabilitiesQueryKey(id) });
  void qc.invalidateQueries({ queryKey: UPSTREAMS_CATALOG_QUERY_KEY });
  void qc.invalidateQueries({ queryKey: skuRoutingSummaryQueryKey(id) });
}

export function useBatchProbeUpstreamCapabilities(
  id: string,
): UseMutationResult<
  UpstreamModelCapabilityRecord[],
  Error,
  readonly string[]
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (capabilityIds) => {
      const res = await batchProbeUpstreamCapabilities(id, capabilityIds, fetchImpl);
      return res.results.map((result) => result.capability);
    },
    onSuccess: () => invalidateCapabilityQueries(qc, id),
  });
}

export function useBatchSetUpstreamCapabilityProbeProtocol(
  id: string,
): UseMutationResult<
  UpstreamModelCapabilityRecord[],
  Error,
  { capability_ids: readonly string[] } & SetUpstreamCapabilityProbeProtocolRequest
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ capability_ids, ...body }) => {
      const res = await batchSetUpstreamCapabilityProbeProtocol(id, capability_ids, body, fetchImpl);
      return res.results.map((result) => result.capability);
    },
    onSuccess: () => invalidateCapabilityQueries(qc, id),
  });
}

export function useBatchIgnoreUpstreamCapabilities(
  id: string,
): UseMutationResult<
  UpstreamModelCapabilityRecord[],
  Error,
  readonly string[]
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (capabilityIds) => {
      const res = await batchIgnoreUpstreamCapabilities(id, capabilityIds, fetchImpl);
      return res.results.map((result) => result.capability);
    },
    onSuccess: () => invalidateCapabilityQueries(qc, id),
  });
}

export function useBatchUnignoreUpstreamCapabilities(
  id: string,
): UseMutationResult<
  UpstreamModelCapabilityRecord[],
  Error,
  readonly string[]
> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (capabilityIds) => {
      const res = await batchUnignoreUpstreamCapabilities(id, capabilityIds, fetchImpl);
      return res.results.map((result) => result.capability);
    },
    onSuccess: () => invalidateCapabilityQueries(qc, id),
  });
}

export function useIgnoreUpstreamCapability(
  id: string,
): UseMutationResult<UpstreamModelCapabilityRecord, Error, string> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (capabilityId) => {
      const res = await ignoreUpstreamCapability(id, capabilityId, fetchImpl);
      return res.results[0]!.capability;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: upstreamCapabilitiesQueryKey(id) });
      void qc.invalidateQueries({ queryKey: UPSTREAMS_CATALOG_QUERY_KEY });
    },
  });
}

export function useUnignoreUpstreamCapability(
  id: string,
): UseMutationResult<UpstreamModelCapabilityRecord, Error, string> {
  const { fetchImpl } = useSession();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (capabilityId) => {
      const res = await unignoreUpstreamCapability(id, capabilityId, fetchImpl);
      return res.results[0]!.capability;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: upstreamCapabilitiesQueryKey(id) });
      void qc.invalidateQueries({ queryKey: UPSTREAMS_CATALOG_QUERY_KEY });
    },
  });
}
